import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import marketHolidays from '../../scripts/market-holidays.json'
import marketHours from '../../scripts/market-hours.json'

interface MarketInfo {
  name: string
  countryCode: string
  isOpen: boolean
  timeToOpen: string | null
  timeToClose: string | null
  openTimeLocal: string | null
  closeTimeLocal: string | null
}

export default function MarketHoursTools() {
  const { t } = useTranslation(['common', 'tools'])
  const [marketsStatus, setMarketsStatus] = useState<MarketInfo[]>([])
  const [currentUtcTime, setCurrentUtcTime] = useState<Date | null>(null)
  const [localTime, setLocalTime] = useState<string>('')

  useEffect(() => {
    const updateUtcTime = () => setCurrentUtcTime(new Date())
    updateUtcTime()
    const timeInterval = setInterval(updateUtcTime, 1000)
    return () => clearInterval(timeInterval)
  }, [])

  useEffect(() => {
    const updateLocalTime = () => {
      const now = new Date()
      const hours = now.getHours().toString().padStart(2, '0')
      const minutes = now.getMinutes().toString().padStart(2, '0')
      const seconds = now.getSeconds().toString().padStart(2, '0')
      setLocalTime(`${hours}:${minutes}:${seconds}`)
    }
    updateLocalTime()
    const interval = setInterval(updateLocalTime, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!currentUtcTime) return

    const checkHoliday = (exchange: string): { status: string; open?: string; close?: string } | null => {
      const todayStr = currentUtcTime.toISOString().split('T')[0]
      for (const key in marketHolidays) {
        const holiday = marketHolidays[key as keyof typeof marketHolidays]
        if (holiday.date === todayStr && holiday.exchange === exchange) {
          return {
            status: holiday.status,
            open: 'open' in holiday ? holiday.open : undefined,
            close: 'close' in holiday ? holiday.close : undefined
          }
        }
      }
      return null
    }

    const calculateMarketStatus = (
      exchange: string,
      openHour: number,
      openMinute: number,
      closeHour: number,
      closeMinute: number,
      timezoneOffset: number
    ): { isOpen: boolean; timeToOpen: string | null; timeToClose: string | null; openTimeLocal: string | null; closeTimeLocal: string | null } => {
      const nowUtc = currentUtcTime.getTime()
      const marketLocalTime = new Date(nowUtc - timezoneOffset * 3600000)
      const marketDay = marketLocalTime.getDay()
      const marketLocalHours = marketLocalTime.getHours()
      const marketMinutes = marketLocalTime.getMinutes()
      const marketCurrentTime = marketLocalHours * 60 + marketMinutes
      const marketOpenTime = openHour * 60 + openMinute

      const formatTimeLocal = (utcTimestamp: number): string => {
        const localDate = new Date(utcTimestamp)
        return `${localDate.getHours().toString().padStart(2, '0')}:${localDate.getMinutes().toString().padStart(2, '0')}`
      }

      const getCloseTimeLocal = (marketDate: Date): string => {
        let utcCloseHour = closeHour - timezoneOffset
        if (utcCloseHour >= 24) utcCloseHour -= 24
        else if (utcCloseHour < 0) utcCloseHour += 24
        const closeTimeUtc = new Date(marketDate.getTime() - timezoneOffset * 3600000)
        closeTimeUtc.setUTCHours(utcCloseHour, closeMinute, 0, 0)
        return formatTimeLocal(closeTimeUtc.getTime())
      }

      const getNextOpenUtc = (startDate: Date): number => {
        let utcOpenHour = openHour - timezoneOffset
        if (utcOpenHour >= 24) utcOpenHour -= 24
        else if (utcOpenHour < 0) utcOpenHour += 24
        let nextOpenUtc = new Date(startDate.getTime() - timezoneOffset * 3600000)
        nextOpenUtc.setUTCHours(utcOpenHour, openMinute, 0, 0)
        if (marketCurrentTime >= marketOpenTime) nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1)
        let marketLocalDate = new Date(nextOpenUtc.getTime() + timezoneOffset * 3600000)
        let day = marketLocalDate.getDay()
        while (day === 0 || day === 6) {
          nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1)
          marketLocalDate = new Date(nextOpenUtc.getTime() + timezoneOffset * 3600000)
          day = marketLocalDate.getDay()
        }
        let nextDateStr = marketLocalDate.toISOString().split('T')[0]
        let nextHoliday: { status: string } | null = null
        for (const key in marketHolidays) {
          const h = marketHolidays[key as keyof typeof marketHolidays]
          if (h.date === nextDateStr && h.exchange === exchange) {
            nextHoliday = h
            break
          }
        }
        while (nextHoliday?.status === 'closed') {
          nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1)
          marketLocalDate = new Date(nextOpenUtc.getTime() + timezoneOffset * 3600000)
          day = marketLocalDate.getDay()
          while (day === 0 || day === 6) {
            nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1)
            marketLocalDate = new Date(nextOpenUtc.getTime() + timezoneOffset * 3600000)
            day = marketLocalDate.getDay()
          }
          nextDateStr = marketLocalDate.toISOString().split('T')[0]
          nextHoliday = null
          for (const key in marketHolidays) {
            const h = marketHolidays[key as keyof typeof marketHolidays]
            if (h.date === nextDateStr && h.exchange === exchange) {
              nextHoliday = h
              break
            }
          }
        }
        return nextOpenUtc.getTime()
      }

      const holiday = checkHoliday(exchange)
      const isMarketOpenLocal = (openStr: string | null, closeStr: string | null): boolean => {
        if (!openStr || !closeStr) return false
        const now = new Date()
        const [oH, oM] = openStr.split(':').map(Number)
        const [cH, cM] = closeStr.split(':').map(Number)
        const cur = now.getHours() * 60 + now.getMinutes()
        const open = oH * 60 + oM
        const close = cH * 60 + cM
        const d = now.getDay()
        if (d < 1 || d > 5) return false
        return cur >= open && cur < close
      }
      const timeToOpenFromLocal = (openStr: string | null, closeStr: string | null): string | null => {
        if (!openStr || isMarketOpenLocal(openStr, closeStr)) return null
        const now = new Date()
        const [oH, oM] = openStr.split(':').map(Number)
        const todayOpen = new Date(now)
        todayOpen.setHours(oH, oM, 0, 0)
        let next = now >= todayOpen ? new Date(todayOpen.getTime() + 86400000) : todayOpen
        while (next.getDay() === 0 || next.getDay() === 6) next = new Date(next.getTime() + 86400000)
        const diff = next.getTime() - now.getTime()
        const h = Math.floor(diff / 3600000)
        const m = Math.floor((diff % 3600000) / 60000)
        return `${h}h ${m}m`
      }
      const timeToCloseFromLocal = (closeStr: string | null): string | null => {
        if (!closeStr) return null
        const now = new Date()
        const [cH, cM] = closeStr.split(':').map(Number)
        const todayClose = new Date(now)
        todayClose.setHours(cH, cM, 0, 0)
        const next = now >= todayClose ? new Date(todayClose.getTime() + 86400000) : todayClose
        const diff = next.getTime() - now.getTime()
        const h = Math.floor(diff / 3600000)
        const m = Math.floor((diff % 3600000) / 60000)
        return `${h}h ${m}m`
      }

      if (holiday?.status === 'closed') {
        const nextOpenUtc = getNextOpenUtc(marketLocalTime)
        const openTimeLocal = formatTimeLocal(nextOpenUtc)
        const closeTimeLocal = getCloseTimeLocal(marketLocalTime)
        return { isOpen: false, timeToOpen: timeToOpenFromLocal(openTimeLocal, closeTimeLocal), timeToClose: null, openTimeLocal, closeTimeLocal }
      }
      if (holiday?.status === 'early-close' && holiday.open && holiday.close) {
        const openTime = new Date(holiday.open).getTime()
        const closeTime = new Date(holiday.close).getTime()
        if (nowUtc >= openTime && nowUtc < closeTime) {
          const openTimeLocal = formatTimeLocal(openTime)
          const closeTimeLocal = formatTimeLocal(closeTime)
          return { isOpen: true, timeToOpen: null, timeToClose: timeToCloseFromLocal(closeTimeLocal), openTimeLocal, closeTimeLocal }
        }
        if (nowUtc < openTime) {
          const openTimeLocal = formatTimeLocal(openTime)
          const closeTimeLocal = formatTimeLocal(closeTime)
          return { isOpen: false, timeToOpen: timeToOpenFromLocal(openTimeLocal, closeTimeLocal), timeToClose: null, openTimeLocal, closeTimeLocal }
        }
        const nextOpenUtc = getNextOpenUtc(marketLocalTime)
        const openTimeLocal = formatTimeLocal(nextOpenUtc)
        const closeTimeLocal = getCloseTimeLocal(marketLocalTime)
        return { isOpen: false, timeToOpen: timeToOpenFromLocal(openTimeLocal, closeTimeLocal), timeToClose: null, openTimeLocal, closeTimeLocal }
      }

      const todayOpenUtc = getNextOpenUtc(new Date(marketLocalTime.getFullYear(), marketLocalTime.getMonth(), marketLocalTime.getDate()))
      const openTimeLocal = formatTimeLocal(todayOpenUtc)
      const closeTimeLocal = getCloseTimeLocal(marketLocalTime)
      const isOpenLocal = isMarketOpenLocal(openTimeLocal, closeTimeLocal)

      if (marketDay >= 1 && marketDay <= 5) {
        if (isOpenLocal) {
          return { isOpen: true, timeToOpen: null, timeToClose: timeToCloseFromLocal(closeTimeLocal), openTimeLocal, closeTimeLocal }
        }
        const nextOpenUtc = getNextOpenUtc(marketLocalTime)
        const nextOpenTimeLocal = formatTimeLocal(nextOpenUtc)
        return { isOpen: false, timeToOpen: timeToOpenFromLocal(nextOpenTimeLocal, closeTimeLocal), timeToClose: null, openTimeLocal, closeTimeLocal }
      }
      const nextOpenUtc = getNextOpenUtc(marketLocalTime)
      const oLocal = formatTimeLocal(nextOpenUtc)
      const cLocal = getCloseTimeLocal(marketLocalTime)
      return { isOpen: false, timeToOpen: timeToOpenFromLocal(oLocal, cLocal), timeToClose: null, openTimeLocal: oLocal, closeTimeLocal: cLocal }
    }

    const updateMarketsStatus = () => {
      const exchanges: (keyof typeof marketHours)[] = ['NYSE', 'NASDAQ', 'LSE', 'TSE', 'XETR', 'HKEX', 'ASX', 'TSX', 'BIST']
      const markets: MarketInfo[] = exchanges.map((key) => {
        const m = marketHours[key]
        return {
          name: m.name,
          countryCode: m.countryCode,
          ...calculateMarketStatus(key, m.openHour, m.openMinute, m.closeHour, m.closeMinute, m.timezoneOffset)
        }
      })
      setMarketsStatus(markets)
    }

    updateMarketsStatus()
    const interval = setInterval(updateMarketsStatus, 1000)
    return () => clearInterval(interval)
  }, [currentUtcTime])

  return (
    <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-lg md:text-xl font-semibold">{t('tools:marketHours.title')}</h2>
        <div className="text-sm text-gray-600">
          <span className="font-medium">{t('tools:marketHours.localTime')} </span>
          <span className="font-mono">{localTime}</span>
        </div>
      </div>
      <div className="space-y-1">
        {marketsStatus.map((market, index) => (
          <div
            key={index}
            className="flex flex-col sm:flex-row sm:items-center justify-between py-2 border-b border-gray-100 last:border-b-0 gap-1 sm:gap-0"
          >
            <div className="flex items-center gap-2">
              <ReactCountryFlag countryCode={market.countryCode} svg style={{ width: '1.2em', height: '1.2em' }} title={market.countryCode} />
              <span className="text-sm font-medium text-gray-700">{market.name}</span>
              {market.openTimeLocal && market.closeTimeLocal && (
                <span className="text-[10px] md:text-xs text-gray-500 font-normal">
                  ({market.openTimeLocal} - {market.closeTimeLocal})
                </span>
              )}
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-2">
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${market.isOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {market.isOpen ? t('common:status.open') : t('common:status.closed')}
              </span>
              {market.isOpen && market.timeToClose && <span className="text-[10px] md:text-xs text-gray-500">{market.timeToClose}</span>}
              {!market.isOpen && market.timeToOpen && <span className="text-[10px] md:text-xs text-gray-500">{market.timeToOpen}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
